import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberMutationReadDataSchema } from './response';

// ─── member ↔ Healthie chart link ───────────────────────────────────────────
//
// Wire vocabulary for linking a Galapagos member to the Healthie client whose
// chart their documents file into. The outcome enum is closed: the consumer,
// the staff routes and Atlas all speak it, and the metric tag derives from it.

/**
 * Every way a link attempt can end. `linked_existing` / `created` /
 * `manual_set` wrote the id; `skipped_*` wrote nothing and is a legitimate
 * result (rendered, not retried); `failed_*` wrote nothing because the write
 * itself was refused.
 */
export const HEALTHIE_LINK_OUTCOME = [
  'linked_existing',
  'linked_crosswalk',
  'linked_record_identifier',
  'created',
  'manual_set',
  'skipped_already_linked',
  'skipped_no_match',
  'skipped_ambiguous',
  'skipped_identity_mismatch',
  'skipped_insufficient_profile',
  'skipped_disabled',
  'skipped_invalid_actor',
  'failed_collision',
  'failed_id_taken',
  'failed_not_found',
] as const;
export const healthieLinkOutcomeSchema = z.enum(HEALTHIE_LINK_OUTCOME);
export type HealthieLinkOutcome = z.infer<typeof healthieLinkOutcomeSchema>;

/** Who asked for the link: the registration-completion event or a staff action. */
export const HEALTHIE_LINK_TRIGGER = ['event', 'staff', 'migration', 'record_identifier'] as const;
export const healthieLinkTriggerSchema = z.enum(HEALTHIE_LINK_TRIGGER);
export type HealthieLinkTrigger = z.infer<typeof healthieLinkTriggerSchema>;

/** Healthie client ids are short opaque tokens; the pattern rejects anything URL-shaped. */
export const HEALTHIE_USER_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
/** Upper bound on a pasted Healthie client id; enforced by the schema and echoed by the Atlas editor. */
export const HEALTHIE_USER_ID_MAX_LENGTH = 64;

/** Body of the staff PATCH that sets the link by hand (the mislink remedy). */
export const healthieLinkSetRequestSchema = z
  .object({
    healthieUserId: z
      .string()
      .min(1)
      .max(HEALTHIE_USER_ID_MAX_LENGTH)
      .regex(HEALTHIE_USER_ID_PATTERN),
  })
  .strict();
export type HealthieLinkSetRequest = z.infer<typeof healthieLinkSetRequestSchema>;

/** Body of the staff POST that runs the automatic link now. */
export const healthieLinkRunDataSchema = z.object({
  outcome: healthieLinkOutcomeSchema,
  member: memberMutationReadDataSchema,
});
export type HealthieLinkRunData = z.infer<typeof healthieLinkRunDataSchema>;

/** Map internal link outcomes onto the wire vocabulary (ADR-135 Rule 3). */
export function toHealthieLinkRunWire(
  outcome: HealthieLinkOutcome,
  member: z.infer<typeof memberMutationReadDataSchema>,
): HealthieLinkRunData {
  if (outcome === 'skipped_identity_mismatch') {
    return { outcome: 'skipped_no_match', member };
  }
  return { outcome, member };
}

export const healthieLinkRunResponseSchema = apiEnvelope(healthieLinkRunDataSchema);
export type HealthieLinkRunResponse = z.infer<typeof healthieLinkRunResponseSchema>;

// ─── the linked chart's email (read) ────────────────────────────────────────
//
// The email Healthie holds for the member's linked client, read live by the
// chart id. Atlas prefills Healthie embed-form links with it: the embed form
// identifies the client by email, and a member can be linked to a chart whose
// email differs from `members.email` (a crosswalk link with no email match),
// so any other address would file the submission under a different client.

/**
 * How the read ended. `resolved` carries the email; `no_chart` means the
 * member has no linked Healthie client; `chart_not_found` means Healthie no
 * longer reports that client; `no_email` means the client holds no email;
 * `not_configured` means this environment has no Healthie configuration (a
 * preview or dev stack without it), which is not an outage.
 */
export const HEALTHIE_CHART_EMAIL_OUTCOME = [
  'resolved',
  'no_chart',
  'chart_not_found',
  'no_email',
  'not_configured',
] as const;
export const healthieChartEmailOutcomeSchema = z.enum(HEALTHIE_CHART_EMAIL_OUTCOME);
export type HealthieChartEmailOutcome = z.infer<typeof healthieChartEmailOutcomeSchema>;

export const healthieChartEmailDataSchema = z.object({
  outcome: healthieChartEmailOutcomeSchema,
  /** The chart's email; non-null exactly when `outcome` is `resolved`. */
  email: z.string().nullable(),
});
export type HealthieChartEmailData = z.infer<typeof healthieChartEmailDataSchema>;

export const healthieChartEmailResponseSchema = apiEnvelope(healthieChartEmailDataSchema);
export type HealthieChartEmailResponse = z.infer<typeof healthieChartEmailResponseSchema>;
